<template>
  <section class="cmp" aria-labelledby="cmp-title">
    <header class="cmp-toolbar">
      <div>
        <h3 id="cmp-title" class="cmp-title">Compara tus opciones</h3>
        <p class="cmp-subtitle">Plan {{ selectedPlanTypeLabel }} · pago {{ selectedPaymentMethod.toLowerCase() }}</p>
      </div>
      <slot name="toolbar" />
    </header>

    <div class="cmp-table" role="table" aria-labelledby="cmp-title" :style="{ '--cmp-cols': columns.length }">
      <div class="cmp-row cmp-head" role="row">
        <div class="cmp-label cmp-label-head" role="columnheader">Coberturas</div>

        <div
          v-for="column in columns"
          :key="column.company"
          class="cmp-company"
          :class="{ 'cmp-company-best': column.isBestPrice, 'cmp-company-off': !column.available }"
          role="columnheader"
        >
          <span v-if="column.isBestPrice" class="cmp-badge">Mejor precio</span>

          <div class="cmp-brand">
            <img
              v-if="column.logoType !== 'chubb'"
              :src="column.logoSrc"
              :alt="`Logo de ${column.name}`"
              class="cmp-logo"
              @error="emit('image-error', column.logoType)"
            />
            <span v-else class="cmp-logo cmp-logo-text" aria-hidden="true">Chubb</span>
            <span class="cmp-company-name">{{ column.name }}</span>
          </div>

          <template v-if="column.available">
            <strong class="cmp-price">${{ formatPrice(column.price) }}</strong>
            <span class="cmp-price-note">
              <template v-if="column.subsequentCount > 0">
                1er pago ${{ formatPrice(column.firstPayment) }} + {{ column.subsequentCount }} de ${{ formatPrice(column.subsequentPayment) }}
              </template>
              <template v-else>Prima anual</template>
            </span>

            <el-button
              v-if="column.emittable"
              type="primary"
              class="cmp-cta"
              @click="emit('emitir', column.company)"
            >
              {{ allowEmission ? 'Emitir ahora' : 'Me interesa' }}
            </el-button>
            <span v-else class="cmp-note">Cotización informativa, sin emisión en línea</span>
          </template>

          <template v-else>
            <strong class="cmp-unavailable">No disponible</strong>
            <span class="cmp-note">No pudimos cotizar con {{ column.name }} para este vehículo. Prueba otro plan o forma de pago.</span>
          </template>
        </div>
      </div>

      <div v-for="row in rows" :key="row.key" class="cmp-row" role="row">
        <div class="cmp-label" role="rowheader">
          <span class="cmp-coverage">{{ row.nombre }}</span>
          <span class="cmp-detail">{{ row.detalle }}</span>
        </div>

        <div
          v-for="column in columns"
          :key="`${row.key}-${column.company}`"
          class="cmp-cell"
          :class="{ 'cmp-cell-best': column.isBestPrice }"
          role="cell"
        >
          <span class="cmp-cell-company">{{ column.name }}</span>
          <span v-if="!column.available" class="cmp-missing" aria-label="Sin datos">—</span>
          <template v-else-if="row.cells[column.company]">
            <span class="cmp-suma">{{ row.cells[column.company]?.suma }}</span>
            <span class="cmp-deducible" :class="{ 'cmp-deducible-none': row.cells[column.company]?.sinDeducible }">
              {{ row.cells[column.company]?.sinDeducible ? 'Sin deducible' : `Deducible ${row.cells[column.company]?.deducible}` }}
            </span>
          </template>
          <span v-else class="cmp-missing">No incluida</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { ComparisonColumn, ComparisonRow } from '@/composables/resultsView/coverageComparison';
import type { ResultsCompany, ResultsLogoType } from './results-view.types';

interface Props {
  columns: ComparisonColumn[];
  rows: ComparisonRow[];
  selectedPlanTypeLabel: string;
  selectedPaymentMethod: string;
  allowEmission: boolean;
  formatPrice: (value: number | undefined | null) => string;
}

defineProps<Props>();

const emit = defineEmits<{
  'image-error': [logoType: ResultsLogoType];
  emitir: [company: ResultsCompany];
}>();
</script>

<style scoped src="@/styles/results-comparison.css"></style>
